import { useRef, useState } from "react";
import { Page as PDFPage } from "react-pdf";
import { X } from "lucide-react";
import { pdfToScreen, screenToPdf } from "@/utils/pdfCoords";

/**
 * One rendered template page with an absolutely-positioned, draggable/resizable
 * overlay for each logo assigned to this page. Geometry is stored in PDF points
 * (bottom-left origin) and translated to/from screen pixels here.
 */
export function LogoCanvas({ pageNumber, pageIndex, width, logos, selected, onSelect, onChange }) {
  const [render, setRender] = useState(null); // { scale, pageHeightPt }
  const containerRef = useRef(null);

  const onPageLoad = (page) => {
    const scale = width / page.originalWidth;
    setRender({ scale, pageHeightPt: page.originalHeight, renderedW: width, renderedH: page.originalHeight * scale });
  };

  const pageLogos = logos
    .map((logo, idx) => ({ logo, idx }))
    .filter(({ logo }) => (logo.page ?? 0) === pageIndex);

  return (
    <div className="relative inline-block shadow-lg" ref={containerRef}>
      <PDFPage
        pageNumber={pageNumber}
        width={width}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onLoadSuccess={onPageLoad}
      />
      {render &&
        pageLogos.map(({ logo, idx }) => (
          <LogoBox
            key={idx}
            src={logo.data}
            box={pdfToScreen(logo, render.scale, render.pageHeightPt)}
            bounds={{ w: render.renderedW, h: render.renderedH }}
            selected={selected === idx}
            onSelect={() => onSelect(idx)}
            onCommit={(screenBox) =>
              onChange(idx, screenToPdf(screenBox, render.scale, render.pageHeightPt))
            }
            onRemove={() => onChange(idx, null)}
          />
        ))}
    </div>
  );
}

function LogoBox({ src, box, bounds, selected, onSelect, onCommit, onRemove }) {
  // Live screen rect while dragging/resizing; null = derive from props.
  const [live, setLive] = useState(null);
  const dragRef = useRef(null);
  const rect = live ?? box;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const startDrag = (e, mode) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onSelect();
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, base: { ...rect } };
  };

  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (d.mode === "move") {
      setLive({
        ...d.base,
        left: clamp(d.base.left + dx, 0, bounds.w - d.base.width),
        top: clamp(d.base.top + dy, 0, bounds.h - d.base.height),
      });
    } else {
      // resize from bottom-right, keep min size, stay within page
      setLive({
        ...d.base,
        width: clamp(d.base.width + dx, 16, bounds.w - d.base.left),
        height: clamp(d.base.height + dy, 16, bounds.h - d.base.top),
      });
    }
  };

  const endDrag = () => {
    if (dragRef.current && live) onCommit(live);
    dragRef.current = null;
    setLive(null);
  };

  return (
    <div
      onPointerDown={(e) => startDrag(e, "move")}
      onPointerMove={onMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
      className={`absolute cursor-move touch-none select-none ${
        selected ? "ring-2 ring-brand-600" : "ring-1 ring-border/60"
      }`}
    >
      <img src={src} alt="logo" draggable={false} className="pointer-events-none h-full w-full object-contain" />
      {selected && (
        <>
          <button
            type="button"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="absolute -right-2 -top-2 rounded-full bg-destructive p-0.5 text-white shadow"
            title="Remove logo"
          >
            <X size={12} />
          </button>
          <div
            onPointerDown={(e) => startDrag(e, "resize")}
            className="absolute -bottom-1.5 -right-1.5 h-3 w-3 cursor-nwse-resize rounded-sm bg-brand-600"
            title="Resize"
          />
        </>
      )}
    </div>
  );
}
